import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { API_URL } from '../utils';
import { computePageStats } from '../utils/pageStats';

/**
 * This page's own timings (KPR-543), read from the browser when asked for.
 * `read()` returns { total, network, server, client, other, ... } or null (no
 * API request in this view yet, or no Performance API). It marks where the
 * view started (0 on a load, the route change after) and when the last API
 * answer reached the screen, and reads the browser's own Resource Timing
 * buffer. It sends nothing and stores nothing.
 */
export default function usePageStats() {
	const { pathname } = useLocation();
	const viewStart = useRef(0);
	const paintedAt = useRef(0);
	const lastPath = useRef(pathname);

	if (lastPath.current !== pathname) {
		lastPath.current = pathname;
		viewStart.current = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
		paintedAt.current = 0;
	}

	useEffect(() => {
		if (typeof PerformanceObserver === 'undefined' || typeof requestAnimationFrame === 'undefined') return undefined;
		let observer;
		try {
			observer = new PerformanceObserver(list => {
				const answered = list.getEntries().some(e => typeof e.name === 'string' && e.name.includes(API_URL));
				if (!answered) return;
				// Two frames on: the data has been drawn.
				requestAnimationFrame(() => requestAnimationFrame(() => { paintedAt.current = performance.now(); }));
			});
			observer.observe({ type: 'resource', buffered: true });
		} catch {
			return undefined;
		}
		return () => observer.disconnect();
	}, []);

	return useCallback(() => {
		if (typeof performance === 'undefined' || !performance.getEntriesByType) return null;
		return computePageStats({
			entries: performance.getEntriesByType('resource'),
			apiUrl: API_URL,
			viewStart: viewStart.current,
			paintedAt: paintedAt.current,
		});
	}, []);
}
